<template>
    <div class="indicator-card cursor-pointer" :class="{ 'loading': isLoading }" @click="handleInfoClick">
        <div class="background-overlay"></div>
        <div class="card-content">
            <div v-if="isLoading" class="spinner-container">
                <div class="spinner">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="sr-only">Cargando...</span>
                    </div>
                </div>
            </div>
            <div v-else class="content">
                <span class="count">{{ formattedDisplayValue }}</span>
                <el-tooltip :content="title" placement="top" effect="light">
                    <span class="title">{{ title }}</span>
                </el-tooltip>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'IndicatorCard',
    props: {
        title: {
            type: String,
            required: true
        },
        count: {
            type: [String, Number],
            required: true
        },
        chartKey: {
            type: String,
            required: true
        },
        isLoading: { type: Boolean, default: false },
    },
    data() {
        return {
            displayValue: 0,
            intervalId: null
        };
    },
    mounted() {
        this.animateValue(0, this.count);
    },
    beforeUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    },
    computed: {
        formattedDisplayValue() {
            return this.formatNumber(Math.round(this.displayValue));
        }
    },
    methods: {
        async handleInfoClick() {
            await this.$emit('chart-info-click', {
                key: this.chartKey,
                title: this.title
            });
        },
        formatNumber(value) {
            return new Intl.NumberFormat('es-CO', {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            }).format(value);
        },
        animateValue(start, end) {
            if (this.intervalId) {
                clearInterval(this.intervalId);
            }
            start = Number(start);
            end = Number(end);
            const duration = Math.min(1000 + Math.log10(end) * 200, 2000);
            const frameRate = 30;
            const totalFrames = Math.round((duration / 1000) * frameRate);
            let currentFrame = 0;
            const increment = (end - start) / totalFrames;
            this.displayValue = start;
            this.intervalId = setInterval(() => {
                currentFrame++;
                this.displayValue = start + increment * currentFrame;
                if (currentFrame >= totalFrames) {
                    clearInterval(this.intervalId);
                    this.displayValue = end;
                }
            }, duration / totalFrames);
        },
    },
    watch: {
        value: {
            handler(newValue) {
                this.animateValue(this.displayValue, newValue);
            },
        },
    },
}
</script>

<style scoped>
.indicator-card {
    width: 210px;
    height: 94px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: rgba(207, 220, 229, 0.945);
    border: 2px solid rgba(255, 255, 255, 1);
    border-radius: 25px;
    overflow: hidden;
    position: relative;
    padding: 10px;
}

.background-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(207, 220, 229, 0.945);
    z-index: 1;
    border-radius: 25px;
}

.card-content {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
}

.spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.spinner {
    display: flex;
    align-items: center;
    justify-content: center;
}

.spinner-border {
    width: 2rem;
    height: 2rem;
    border: 0.25em solid rgba(4, 55, 61, 0.2);
    border-right-color: rgba(4, 55, 61, 1);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.count {
    color: rgba(4, 55, 61, 1);
    font-family: Poppins;
    font-weight: bold;
    font-size: 40px;
    line-height: 1;
}

.title {
    color: rgba(4, 55, 61, 1);
    font-family: Poppins;
    font-weight: medium;
    font-size: 16px;
    line-height: 1.2;
    word-wrap: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

@media (max-width: 768px) {
    .indicator-card {
        width: 160px;
        height: 80px;
        padding: 8px;
    }

    .count {
        font-size: 40px;
    }

    .title {
        font-size: 14px;
        -webkit-line-clamp: 2;
    }

    .spinner-border {
        width: 1.5rem;
        height: 1.5rem;
    }
}

@media (max-width: 480px) {
    .indicator-card {
        width: 120px;
        height: 60px;
        padding: 6px;
    }

    .count {
        font-size: 30px;
    }

    .title {
        font-size: 12px;
        -webkit-line-clamp: 1;
    }

    .spinner-border {
        width: 1rem;
        height: 1rem;
    }
}
</style>
